import { PenLine, TimerReset } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { ListRow } from "./list-row";
import { T } from "./signatures-expiring-widget.i18n";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

/** Within this many hours a link is urgent and shows red rather than amber. */
const URGENT_HOURS = 12;

export function SignaturesExpiringWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("signatures_expiring", target);
  const t = useT(T);
  const { count, relative, toDate } = useDashboardFormat();
  const now = new Date();

  return (
    <WidgetCard
      icon={TimerReset}
      title={t.signaturesExpiring}
      description={t.signaturesExpiringDescription}
      badge={data ? count(data.total) : undefined}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.items.length === 0 ? (
          <WidgetEmpty>{t.noExpiring}</WidgetEmpty>
        ) : (
          <div className="space-y-3">
            {data.items.map((item) => {
              const hoursLeft = (toDate(item.expiresAt).getTime() - now.getTime()) / 3_600_000;
              const urgent = hoursLeft < URGENT_HOURS;
              return (
                <ListRow
                  key={item.signatureOrderId}
                  icon={PenLine}
                  tone={urgent ? "destructive" : "warning"}
                  title={item.clientName}
                  badge={
                    <Badge variant={urgent ? "destructive" : "warning"}>
                      {relative(item.expiresAt, now)}
                    </Badge>
                  }
                  detail={[item.flowName, item.ownerName].filter(Boolean).join(" · ")}
                  meta={t.sent(relative(item.sentAt, now))}
                  to={item.link}
                />
              );
            })}
          </div>
        ))}
    </WidgetCard>
  );
}
